import * as React from 'react';

import {
  Button,
  Checkbox,
  ConsoleDialog,
  PhoneField,
  Row,
  TextField,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { ContactRecord } from '../../_graphql/queryAccounts';
import { ContactFormState } from '../../_state/_types';
import * as accountEditingState from '../../_state/accountEditing.state';
import { useTranslations } from '../../i18n/useTranslations';

export type ContactFormDialogProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  contact?: ContactRecord;
};

export const ContactFormDialog = ({
  isOpen,
  onOpenChange,
}: ContactFormDialogProps) => {
  const t = useTranslations();

  const canSubmitContactForm = useAtomValue(
    accountEditingState.$canSubmitContactForm,
  );
  const shouldDisablePrimaryCheckbox = useAtomValue(
    accountEditingState.$shouldDisablePrimaryCheckbox,
  );
  const { isPending: isCreatingContact } = useAtomValue(
    accountEditingState.$updateRemoteAccount,
  );

  const createContact = useSetAtom(accountEditingState.$createContact);
  const updateContact = useSetAtom(accountEditingState.$updateContact);

  const [contactForm, updateContactForm] = useAtom(
    accountEditingState.$contactForm,
  );

  const isEditing = !!contactForm.id;

  const hasUnsavedChanges = useAtomValue(
    accountEditingState.$hasUnsavedContactChanges,
  );

  const handleSubmit = async () => {
    if (isEditing) {
      await updateContact();
    } else {
      await createContact();
    }
    onOpenChange(false);
  };

  const handleChange =
    <T extends keyof ContactFormState>(key: T) =>
    (value: ContactFormState[T]) => {
      updateContactForm({ [key]: value });
    };

  const fieldProps = <T extends keyof ContactFormState>(key: T) => ({
    value: contactForm[key],
    onChange: handleChange(key),
  });
  return (
    <ConsoleDialog
      className="@ContactFormDialog"
      title={
        isEditing
          ? t.accountsView.contacts.dialog.titleEdit
          : t.accountsView.contacts.dialog.titleNew
      }
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      askBeforeClosing={hasUnsavedChanges}
      content={
        <div className={'grid grid-cols-2 gap-4'}>
          <TextField
            label={t.accountsView.form.fields.contacts.firstName}
            autoFocus
            isRequired
            {...fieldProps('firstName')}
          />
          <TextField
            label={t.accountsView.form.fields.contacts.lastName}
            autoComplete="off"
            isRequired
            {...fieldProps('lastName')}
          />
          <TextField
            label={t.accountsView.form.fields.contacts.email}
            type="email"
            isRequired
            {...fieldProps('email')}
          />
          <PhoneField
            label={t.accountsView.form.fields.contacts.phone}
            isRequired
            {...fieldProps('phone')}
          />
          <TextField
            label={t.accountsView.form.fields.contacts.role}
            {...fieldProps('role')}
          />
          <TextField
            label={t.accountsView.form.fields.contacts.department}
            {...fieldProps('department')}
          />
          <TextField
            label={t.accountsView.form.fields.contacts.notes}
            rows={2}
            className="col-span-2"
            {...fieldProps('notes')}
          />
          <Checkbox
            className="col-span-2"
            isSelected={contactForm.isPrimary}
            onChange={handleChange('isPrimary')}
            isDisabled={shouldDisablePrimaryCheckbox}
          >
            {t.accountsView.form.fields.contacts.isPrimary}
          </Checkbox>
        </div>
      }
      footer={
        <Row gap2>
          <Button
            variant="outline"
            color="neutral"
            onClick={() => onOpenChange(false)}
          >
            {t.accountsView.dialog.actions.cancel}
          </Button>
          <Button
            isDisabled={!canSubmitContactForm}
            isLoading={isCreatingContact}
            onClick={handleSubmit}
          >
            {t.accountsView.dialog.actions.save}
          </Button>
        </Row>
      }
    />
  );
};
